<!--
  - SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<div
		v-if="reactionsCount && reactionsSorted"
		class="reactions-wrapper"
		:class="{
			light: isSplitViewEnabled && isSelfActor,
		}">
		<NcPopover
			v-for="reaction in reactionsSorted"
			:key="reaction"
			:delay="200"
			noFocusTrap
			:triggers="['hover']"
			:popperTriggers="['hover']"
			@afterShow="fetchReactions">
			<template #trigger>
				<NcButton
					:variant="userHasReacted(reaction) ? 'primary' : 'secondary'"
					class="reaction-button--trigger"
					size="small"
					@click="handleReactionClick(reaction)">
					<span class="reaction-emoji">{{ reaction }}</span> {{ reactionsCount(reaction) }}
				</NcButton>
			</template>

			<div v-if="hasReactionsLoaded" class="reaction-details">
				<span>{{ getReactionSummary(reaction) }}
					<span v-if="reactionsCount(reaction) === 4">
						{{ remainingReactionsLabel(reaction) }}
					</span>
					<a
						v-else-if="reactionsCount(reaction) > 4"
						class="more-reactions-button"
						role="button"
						tabindex="0"
						@click.prevent="showAllReactions = true">
						{{ remainingReactionsLabel(reaction) }}
					</a>
				</span>
			</div>
			<div v-else class="details-loading">
				<NcLoadingIcon />
			</div>
		</NcPopover>

		<!-- all reactions button -->
		<NcButton
			class="reaction-controls"
			:class="{ 'reaction-controls--hidden': !showControls }"
			size="small"
			:title="t('spreed', 'Show all reactions')"
			:aria-label="t('spreed', 'Show all reactions')"
			@click="showAllReactions = true">
			<IconHeartOutline :size="15" />
		</NcButton>

		<!-- More reactions picker -->
		<NcEmojiPicker
			v-if="canReact"
			:perLine="5"
			class="reaction-controls"
			:class="{ 'reaction-controls--hidden': !showControls }"
			@select="handleReactionClick"
			@afterShow="emitEmojiPickerStatus"
			@afterHide="emitEmojiPickerStatus">
			<NcButton
				size="small"
				class="reaction-button--trigger"
				:title="t('spreed', 'Add more reactions')"
				:aria-label="t('spreed', 'Add more reactions')">
				<IconEmoticonPlusOutline :size="15" />
			</NcButton>
		</NcEmojiPicker>

		<!-- all reactions modal-->
		<ReactionsList
			v-if="showAllReactions"
			:token="token"
			:detailedReactions="detailedReactions"
			:reactionsSorted="reactionsSorted"
			@close="showAllReactions = false" />
	</div>
</template>

<script>
import { showError } from '@nextcloud/dialogs'
import { n, t } from '@nextcloud/l10n'
import { emojiAddRecent } from '@nextcloud/vue/functions/emoji'
import { inject } from 'vue'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcEmojiPicker from '@nextcloud/vue/components/NcEmojiPicker'
import NcLoadingIcon from '@nextcloud/vue/components/NcLoadingIcon'
import NcPopover from '@nextcloud/vue/components/NcPopover'
import IconEmoticonPlusOutline from 'vue-material-design-icons/EmoticonPlusOutline.vue'
import IconHeartOutline from 'vue-material-design-icons/HeartOutline.vue'
import ReactionsList from './ReactionsList.vue'
import { ATTENDEE } from '../../../../../constants.ts'
import { useActorStore } from '../../../../../stores/actor.ts'
import { useGuestNameStore } from '../../../../../stores/guestName.ts'
import { useReactionsStore } from '../../../../../stores/reactions.js'
import { getDisplayNameWithFallback } from '../../../../../utils/getDisplayName.ts'

export default {
	name: 'ReactionsWrapper',

	components: {
		NcButton,
		NcEmojiPicker,
		NcLoadingIcon,
		NcPopover,
		ReactionsList,
		IconEmoticonPlusOutline,
		IconHeartOutline,
	},

	props: {
		token: {
			type: String,
			required: true,
		},

		/**
		 * Whether the current user can react to the message.
		 */
		canReact: {
			type: Boolean,
			default: false,
		},

		/**
		 * The message id.
		 */
		id: {
			type: [String, Number],
			required: true,
		},

		showControls: {
			type: Boolean,
			default: false,
		},

		isSelfActor: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['emojiPickerToggled'],

	setup() {
		const isSplitViewEnabled = inject('messagesList:isSplitViewEnabled', true)
		return {
			guestNameStore: useGuestNameStore(),
			reactionsStore: useReactionsStore(),
			actorStore: useActorStore(),
			isSplitViewEnabled,
		}
	},

	data() {
		return {
			showAllReactions: false,
		}
	},

	computed: {
		hasReactionsLoaded() {
			return Object.keys(Object(this.detailedReactions)).length !== 0
		},

		detailedReactions() {
			return this.reactionsStore.getReactions(this.token, this.id)
		},

		plainReactions() {
			return this.$store.getters.message(this.token, this.id).reactions
		},

		reactionsSelf() {
			return this.$store.getters.message(this.token, this.id).reactionsSelf
		},

		reactionsSorted() {
			if (this.detailedReactions) {
				return Object.keys(this.detailedReactions)
					.sort((a, b) => this.detailedReactions[b].length - this.detailedReactions[a].length)
			} else if (this.plainReactions) {
				return Object.keys(this.plainReactions)
					.sort((a, b) => this.plainReactions[b] - this.plainReactions[a])
			}
			return undefined
		},
	},

	methods: {
		t,
		n,

		fetchReactions() {
			if (this.hasOutdatedDetails()) {
				this.reactionsStore.fetchReactions(this.token, this.id)
			}
		},

		/**
		 * Compare the plain reactions with the simplified detailed reactions.
		 */
		hasOutdatedDetails() {
			if (!this.hasReactionsLoaded) {
				// No details available yet
				return true
			}

			const allPlainReactions = Object.keys(this.plainReactions)

			// If the keys differ, we have outdated details
			return allPlainReactions.length !== Object.keys(this.detailedReactions).length
				|| allPlainReactions.some((reaction) => this.plainReactions[reaction] !== this.detailedReactions[reaction]?.length)
		},

		userHasReacted(reaction) {
			return this.reactionsSelf?.includes(reaction)
		},

		async handleReactionClick(clickedEmoji) {
			if (!this.canReact) {
				showError(t('spreed', 'No permission to post reactions in this conversation'))
				return
			}

			// Check if current user has already added this reaction to the message
			if (!this.userHasReacted(clickedEmoji)) {
				this.reactionsStore.addReactionToMessage({
					token: this.token,
					messageId: this.id,
					selectedEmoji: clickedEmoji,
				})
				// Update value in frequently used emoji
				emojiAddRecent(clickedEmoji)
			} else {
				this.reactionsStore.removeReactionFromMessage({
					token: this.token,
					messageId: this.id,
					selectedEmoji: clickedEmoji,
				})
			}
		},

		getDisplayNameForReaction(reactingParticipant) {
			if (reactingParticipant.actorType === ATTENDEE.ACTOR_TYPE.GUESTS) {
				return this.guestNameStore.getGuestNameWithGuestSuffix(this.token, reactingParticipant.actorId)
			}

			return getDisplayNameWithFallback(reactingParticipant.actorDisplayName, reactingParticipant.actorType)
		},

		reactionsCount(reaction) {
			if (!this.detailedReactions || !this.plainReactions) {
				return undefined
			}
			return this.detailedReactions
				? this.detailedReactions[reaction]?.length
				: this.plainReactions[reaction]
		},

		getReactionSummary(reaction) {
			// Check if the reaction details are loaded
			if (!this.hasReactionsLoaded) {
				return ''
			}
			const list = this.detailedReactions[reaction].slice(0, 3)
			const summary = []

			for (const item in list) {
				if (this.actorStore.checkIfSelfIsActor(list[item])) {
					summary.unshift(t('spreed', 'You'))
				} else {
					summary.push(this.getDisplayNameForReaction(list[item]))
				}
			}
			return summary.join(', ')
		},

		emitEmojiPickerStatus() {
			this.$emit('emojiPickerToggled')
		},

		remainingReactionsLabel(reaction) {
			const reactionsCount = this.reactionsCount(reaction)
			if (reactionsCount === 4) {
				return t('spreed', 'and {participant}', { participant: this.getDisplayNameForReaction(this.detailedReactions[reaction][3]) })
			}
			return n('spreed', 'and %n other participant', 'and %n other participants', this.reactionsCount(reaction) - 3)
		},
	},
}
</script>

<style lang="scss" scoped>
.reactions-wrapper {
	--minimal-button-width: 48px;
	--font-family-emoji: 'Segoe UI Emoji', 'Segoe UI Symbol', 'Segoe UI', 'Apple Color Emoji', 'Twemoji Mozilla', 'Noto Color Emoji', 'EmojiOne Color', 'Android Emoji';
	display: contents;

	// Overwrite NcButton styles
	:deep(.button-vue) {
		min-width: var(--minimal-button-width);
	}

	&.light :deep(.button-vue--secondary) {
		background-color: var(--color-primary-element-extra-light);
		&:hover {
			background-color: var(--color-primary-element-extra-light-hover);
		}
	}

	.reaction-emoji {
		font-family: var(--font-family-emoji);
	}

	.reaction-button--trigger {
		height: 100%;
	}
}

.reaction-details {
	padding: 8px;
	max-width: 250px;
}

.details-loading {
	display: flex;
	justify-content: center;
	width: 38px;
}

.more-reactions-button {
	text-decoration: underline;
	&:hover {
		text-decoration: none;
	}
}

// Split view
.reactions-wrapper {
	.reaction-button--trigger {
		padding-inline: 4px;
	}

	.reaction-controls {
		align-self: stretch;

		&--hidden {
			visibility: hidden;
		}
	}
}
</style>
